import { type PropsWithChildren, type ReactNode } from "react"
import { Accordion, Card, Stack, Typography } from "@octopus-energy/coral/atoms"
import { FormikRadio, FormikRadioOption } from "@octopus-energy/coral/formik"
import { useTranslations } from "next-intl"
import { SubscriptionContainer } from "@/features/subscription"
import { formatPaymentAmount } from "@/utils/format-payment-amount"
import { type OnboardingFormValues } from "../../wizard"

function BoldTertiaryText(chunk: ReactNode) {
    return (
        <Typography
            component="span"
            color="tertiary500"
        >
            <b>{chunk}</b>
        </Typography>
    )
}

function MeterPowerFormContainer({ children }: PropsWithChildren) {
    const t = useTranslations("MeterPowerForm")

    return (
        <SubscriptionContainer.Form>
            {children}

            <Accordion
                theme="base500"
                title={t("accordion.title")}
            >
                <Stack
                    flexDirection="column"
                    gap="xs"
                >
                    <Typography>{t("accordion.content1")}</Typography>
                    <Typography>{t("accordion.content2")}</Typography>
                </Stack>
            </Accordion>
        </SubscriptionContainer.Form>
    )
}

type MeterPowerFormProps = {
    meterPowerAnalysis: OnboardingFormValues["meterPowerAnalysis"]
}

export function MeterPowerForm({ meterPowerAnalysis }: MeterPowerFormProps) {
    const t = useTranslations("MeterPowerForm")

    const {
        currentSubscribedMaxPower,
        recommendedSubscribedMaxPower,
        suggestedPowerAdjustment,
        yearlyProfitAmountCents,
    } = meterPowerAnalysis

    const yearlyProfitAmount = formatPaymentAmount({
        amount: yearlyProfitAmountCents ?? 0,
    })

    if (suggestedPowerAdjustment !== "LOWER") {
        // we don't display the form if the power is adapted
        return null
    }

    return (
        <MeterPowerFormContainer>
            <Typography>
                {t.rich("success.content", {
                    b: BoldTertiaryText,
                    currentSubscribedMaxPower,
                })}
            </Typography>
            <Card
                borderless
                theme="base500"
            >
                <Stack flexDirection="column">
                    <FormikRadio name="electricitySubscribedMaxPower">
                        <Stack flexDirection="column">
                            <FormikRadioOption
                                value={String(currentSubscribedMaxPower)}
                                attributes={{ "data-testid": "currentSubscribedMaxPower" }}
                            >
                                <Typography
                                    textStyle="h5"
                                    component="span"
                                >
                                    {t("form.currentPower", { currentSubscribedMaxPower })}
                                </Typography>
                            </FormikRadioOption>
                            <FormikRadioOption
                                value={String(recommendedSubscribedMaxPower)}
                                attributes={{ "data-testid": "recommendedSubscribedMaxPower" }}
                            >
                                <Typography
                                    textStyle="h5"
                                    component="span"
                                >
                                    {t("form.recommendedPower", {
                                        recommendedSubscribedMaxPower,
                                    })}
                                </Typography>
                            </FormikRadioOption>
                        </Stack>
                    </FormikRadio>

                    <Typography>
                        {t.rich("success.advice", {
                            b: BoldTertiaryText,
                            recommendedSubscribedMaxPower,
                            yearlyProfitAmount,
                        })}
                    </Typography>
                </Stack>
            </Card>
        </MeterPowerFormContainer>
    )
}

MeterPowerForm.messages = ["MeterPowerForm"] as const
